import { useParams, Link } from 'react-router-dom';
import { comics, categories } from '@/mocks/comics';
import { useMemo, useState } from 'react';
import SEOHead from '@/components/feature/SEOHead';

const ITEMS_PER_PAGE = 12;

export default function Category() {
  const { slug = 'all' } = useParams();
  const [currentPage, setCurrentPage] = useState(1);
  const [sortBy, setSortBy] = useState<'latest' | 'popular' | 'rating'>('latest');

  const currentCategory = useMemo(() => {
    return categories.find((c) => c.slug === slug) || categories[0];
  }, [slug]);

  const filteredComics = useMemo(() => {
    let result = slug === 'all' ? [...comics] : comics.filter((c) => c.categorySlug === slug);

    switch (sortBy) {
      case 'popular':
        result.sort((a, b) => b.views - a.views);
        break;
      case 'rating':
        result.sort((a, b) => b.rating - a.rating);
        break;
      case 'latest':
      default:
        result.sort((a, b) => new Date(b.updateDate).getTime() - new Date(a.updateDate).getTime());
        break;
    }
    return result;
  }, [slug, sortBy]);

  const totalPages = Math.ceil(filteredComics.length / ITEMS_PER_PAGE);
  const pagedComics = filteredComics.slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE);

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    'itemListElement': [
      { '@type': 'ListItem', 'position': 1, 'name': '首页', 'item': 'https://www.hanmanzhijia.com' },
      { '@type': 'ListItem', 'position': 2, 'name': '分类浏览', 'item': 'https://www.hanmanzhijia.com/category/all' },
      { '@type': 'ListItem', 'position': 3, 'name': currentCategory.name, 'item': `https://www.hanmanzhijia.com/category/${slug}` },
    ],
  };

  const categorySchema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    'name': `${currentCategory.name} - 韩漫之家`,
    'description': `韩漫之家${currentCategory.name}分类，收录${currentCategory.count}部优质韩国漫画，免费在线浏览。`,
    'url': `https://www.hanmanzhijia.com/category/${slug}`,
    'isPartOf': {
      '@type': 'WebSite',
      'name': '韩漫之家',
      'url': 'https://www.hanmanzhijia.com',
    },
  };

  return (
    <>
      <SEOHead
        title={`${currentCategory.name} - 漫画分类`}
        description={`韩漫之家${currentCategory.name}分类，收录${currentCategory.count}部优质韩国漫画。免费韩漫在线阅读，每日更新，精彩不断。`}
        keywords={`韩漫,韩国漫画,${currentCategory.name}韩漫,免费漫画,在线漫画,韩漫在线阅读`}
        canonicalUrl={`/category/${slug}`}
        schema={categorySchema}
      />

      {/* Breadcrumb */}
      <div className="pt-20 bg-background-50">
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-4">
          <nav className="flex items-center gap-2 text-xs text-foreground-500" aria-label="面包屑导航">
            <Link to="/" className="hover:text-primary-500 transition-colors no-underline cursor-pointer">首页</Link>
            <div className="w-3 h-3 flex items-center justify-center"><i className="ri-arrow-right-s-line text-xs"></i></div>
            <span className="text-foreground-700 font-medium">{currentCategory.name}</span>
          </nav>
        </div>
      </div>

      {/* Category Tabs */}
      <section className="bg-background-50 border-b border-background-200/70">
        <div className="max-w-7xl mx-auto px-4 md:px-6 pb-0">
          <div className="flex items-center gap-1.5 overflow-x-auto pb-4 flex-wrap">
            {categories.map((cat) => (
              <Link
                key={cat.slug}
                to={`/category/${cat.slug}`}
                onClick={() => setCurrentPage(1)}
                className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors cursor-pointer no-underline ${
                  slug === cat.slug
                    ? 'bg-primary-500 text-white'
                    : 'bg-white text-foreground-600 hover:bg-background-100 border border-background-200/70'
                }`}
              >
                <div className="w-4 h-4 flex items-center justify-center"><i className={`${cat.icon} text-xs`}></i></div>
                {cat.name}
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Sort & Results */}
      <section className="py-8 md:py-10 bg-background-50">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
            <div>
              <h1 className="text-xl md:text-2xl font-bold text-foreground-950 font-heading">
                {currentCategory.name}
              </h1>
              <p className="text-sm text-foreground-500 mt-1">
                共 {filteredComics.length} 部作品
              </p>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-xs text-foreground-500 whitespace-nowrap">排序：</span>
              {[
                { key: 'latest' as const, label: '最新' },
                { key: 'popular' as const, label: '最热' },
                { key: 'rating' as const, label: '评分' },
              ].map((opt) => (
                <button
                  key={opt.key}
                  onClick={() => { setSortBy(opt.key); setCurrentPage(1); }}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-colors cursor-pointer ${
                    sortBy === opt.key
                      ? 'bg-secondary-500 text-white'
                      : 'bg-white text-foreground-600 hover:bg-background-100 border border-background-200/70'
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          {pagedComics.length === 0 ? (
            <div className="text-center py-20">
              <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100 text-foreground-400">
                <i className="ri-emotion-sad-line text-2xl"></i>
              </div>
              <p className="text-foreground-500">该分类暂无漫画</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4 md:gap-5" data-product-shop>
              {pagedComics.map((comic) => (
                <Link
                  key={comic.id}
                  to={`/comic/${comic.id}`}
                  className="group bg-white rounded-xl overflow-hidden hover-shimmer transition-colors cursor-pointer no-underline"
                >
                  <div className="relative aspect-[3/4] overflow-hidden">
                    <img
                      src={comic.cover}
                      alt={comic.title}
                      title={`${comic.title} - 韩漫之家免费阅读`}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                    />
                    <div className="absolute top-2 left-2">
                      <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium whitespace-nowrap ${
                        comic.status === '连载中'
                          ? 'bg-accent-500 text-white'
                          : 'bg-foreground-800 text-white'
                      }`}>
                        {comic.status}
                      </span>
                    </div>
                  </div>
                  <div className="p-3">
                    <h3 className="text-sm font-semibold text-foreground-950 mb-1 line-clamp-1 whitespace-nowrap overflow-hidden text-ellipsis">{comic.title}</h3>
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-foreground-500">{comic.author}</span>
                      <div className="flex items-center gap-1">
                        <div className="w-3 h-3 flex items-center justify-center"><i className="ri-star-fill text-yellow-400 text-[10px]"></i></div>
                        <span className="text-xs text-foreground-400">{comic.rating}</span>
                      </div>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-center gap-2 mt-10">
              <button
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                className="w-9 h-9 flex items-center justify-center rounded-lg bg-white border border-background-200/70 text-foreground-600 hover:bg-background-100 disabled:opacity-40 disabled:cursor-not-allowed transition-colors cursor-pointer"
              >
                <i className="ri-arrow-left-s-line"></i>
              </button>
              {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
                <button
                  key={p}
                  onClick={() => setCurrentPage(p)}
                  className={`w-9 h-9 flex items-center justify-center rounded-lg text-sm font-medium whitespace-nowrap transition-colors cursor-pointer ${
                    currentPage === p
                      ? 'bg-primary-500 text-white'
                      : 'bg-white border border-background-200/70 text-foreground-600 hover:bg-background-100'
                  }`}
                >
                  {p}
                </button>
              ))}
              <button
                onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
                className="w-9 h-9 flex items-center justify-center rounded-lg bg-white border border-background-200/70 text-foreground-600 hover:bg-background-100 disabled:opacity-40 disabled:cursor-not-allowed transition-colors cursor-pointer"
              >
                <i className="ri-arrow-right-s-line"></i>
              </button>
            </div>
          )}
        </div>
      </section>

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }}
      />
    </>
  );
}